import {
  Box,
  Checkbox,
  FormControlLabel,
  Typography,
  Paper,
  Grid2,
  Alert,
} from "@mui/material";
import { useState, useEffect } from "react";
import CollapsibleFormElement from "../../common/collapsible-form-element";

export default function PhrasebookBrands({
  availableBrands,
  selectedBrands,
  onChange,
  currentBrandId,
  isOrganizationFiltered,
}: {
  availableBrands: { id: string; name: string }[];
  selectedBrands: string[];
  onChange: (newBrands: string[]) => void;
  currentBrandId?: string;
  isOrganizationFiltered?: boolean;
}) {
  const [brandStates, setBrandStates] = useState<{ [key: string]: boolean }>(
    {}
  );

  // Initialize brand states from selectedBrands
  useEffect(() => {
    const initialStates: { [key: string]: boolean } = {};

    // Initialize all available brands based on selectedBrands
    // Current brand is always selected
    availableBrands.forEach((brand) => {
      initialStates[brand.id] =
        brand.id === currentBrandId || selectedBrands.includes(brand.id);
    });

    setBrandStates(initialStates);
  }, [availableBrands, selectedBrands, currentBrandId]);

  const handleChange =
    (brandId: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
      // Prevent changes to the current brand (it should always be selected)
      if (brandId === currentBrandId) return;

      const newBrandStates = {
        ...brandStates,
        [brandId]: event.target.checked,
      };

      setBrandStates(newBrandStates);

      // Update selected brands by filtering the IDs where value is true
      const newSelectedBrands = Object.keys(newBrandStates).filter(
        (id) => newBrandStates[id]
      );

      onChange(newSelectedBrands);
    };

  const handleSelectAll = () => {
    // For "all selected" check, exclude the current brand (it's always selected)
    const selectableBrands = availableBrands.filter(
      (brand) => brand.id !== currentBrandId
    );
    const allSelected = selectableBrands.every(
      (brand) => brandStates[brand.id]
    );

    // If all are selected, deselect all (except current brand). Otherwise, select all.
    const newState = !allSelected;
    const newBrandStates: { [key: string]: boolean } = {};

    availableBrands.forEach((brand) => {
      // Current brand is always selected
      if (brand.id === currentBrandId) {
        newBrandStates[brand.id] = true;
      } else {
        newBrandStates[brand.id] = newState;
      }
    });

    setBrandStates(newBrandStates);

    if (newState) {
      // All selected
      onChange(availableBrands.map((brand) => brand.id));
    } else {
      // None selected (except current brand)
      onChange(currentBrandId ? [currentBrandId] : []);
    }
  };

  // For "all selected" check, exclude the current brand (it's always selected)
  const selectableBrands = availableBrands.filter(
    (brand) => brand.id !== currentBrandId
  );

  return (
    <CollapsibleFormElement title="Phrasebook Brands">
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ mb: 1, display: "block" }}
      >
        Select the brands that will be used by default as a reference
        for the phrasebook.
      </Typography>

      {isOrganizationFiltered && (
        <Alert severity="info" sx={{ mb: 2 }}>
          This batch belongs to a client organization. Only brands from the same
          organization are shown to ensure compatibility.
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: 2, mt: 2, mb: 2 }}>
        <Box sx={{ mb: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={
                  selectableBrands.length > 0 &&
                  selectableBrands.every((brand) => brandStates[brand.id])
                }
                indeterminate={
                  selectableBrands.some((brand) => brandStates[brand.id]) &&
                  !selectableBrands.every((brand) => brandStates[brand.id])
                }
                onChange={handleSelectAll}
                color="primary"
              />
            }
            label={
              <Typography variant="body2" fontWeight="bold">
                Select all brands
              </Typography>
            }
          />
        </Box>

        <Grid2 container spacing={1}>
          {availableBrands.map((brand) => {
            const isCurrentBrand = brand.id === currentBrandId;
            return (
              <Grid2 size={{ xs: 12, sm: 6, md: 4 }} key={brand.id}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={!!brandStates[brand.id]}
                      onChange={handleChange(brand.id)}
                      color="primary"
                      size="small"
                      disabled={isCurrentBrand}
                    />
                  }
                  label={
                    <Typography
                      variant="body2"
                      noWrap
                      sx={{
                        fontWeight: isCurrentBrand ? "bold" : "normal",
                      }}
                    >
                      {brand.name}
                      {isCurrentBrand && " (current)"}
                    </Typography>
                  }
                  sx={{
                    marginRight: 0,
                    width: "100%",
                    "& .MuiFormControlLabel-label": {
                      width: "calc(100% - 42px)", // Account for checkbox width
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    },
                  }}
                />
              </Grid2>
            );
          })}
        </Grid2>

        {availableBrands.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            No brands available.
          </Typography>
        )}
      </Paper>
    </CollapsibleFormElement>
  );
}
